/**
 * Navigation - Compliance Operating System Sidebar
 * 
 * @description Enterprise-grade navigation implementing the Compliance Operating System model.
 * This is NOT a feature catalog - it's a workflow-driven operating system.
 * 
 * Architecture:
 * - DISCLOSURE CYCLES as hero (primary workflow)
 * - FRAMEWORKS as regulation library
 * - EVIDENCE HUB for "upload once, use everywhere"
 * - OBLIGATIONS ENGINE for "what you owe"
 * - AUDIT & ASSURANCE for defensibility
 * - VALUE CHAIN for external parties
 * - INTELLIGENCE for insights
 * - REPORTS for output
 * - SYSTEM for configuration
 * 
 * @component
 */
import React, { useState, useMemo, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  ChevronDown,
  ChevronRight,
  HelpCircle,
  LogOut,
  User,
  Leaf,
  LayoutDashboard,
  X,
} from 'lucide-react';

import NAVIGATION_CONFIG, { 
  BADGE_STYLES, 
  SECTION_STYLES,
  NavSectionType,
  BadgeType,
  isPathActive,
} from '../config/navigation.config';
import { apiFetch } from '../services/api_service';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Badge component for navigation items
 * Now supports dynamic count badges
 */
const NavBadge = ({ badge, dynamicValue }) => {
  if (!badge && !dynamicValue) return null;
  
  // Dynamic count badge
  if (dynamicValue && dynamicValue > 0) {
    return (
      <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-purple-500/20 text-purple-400">
        {dynamicValue}
      </span>
    );
  }
  
  if (!badge) return null;
  
  const style = BADGE_STYLES[badge.type] || BADGE_STYLES[BadgeType.NEW];
  const displayValue = badge.value || (badge.type === BadgeType.NEW ? 'NEW' : badge.type === BadgeType.AI ? 'AI' : '');
  
  return (
    <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${style}`}>
      {displayValue}
    </span>
  );
};

/**
 * Navigation item component
 */
const NavItem = ({ item, isActive, sectionType }) => {
  const Icon = item.icon;
  const styles = SECTION_STYLES[sectionType] || SECTION_STYLES[NavSectionType.CONFIG];
  
  return (
    <li>
      <Link
        to={item.path}
        data-testid={`nav-item-${item.id}`}
        className={`
          flex items-center gap-3 mx-2 px-3 py-2 text-sm font-medium rounded-lg transition-all
          ${isActive 
            ? 'bg-white/10 text-white border border-white/20' 
            : `text-[#A0A0A0] hover:bg-[#1F1F1F] ${styles.accentClass}`
          }
        `}
        title={item.description}
      >
        <Icon size={16} strokeWidth={1.5} className="flex-shrink-0" />
        <span className="flex-1 truncate">{item.label}</span>
        <NavBadge badge={item.badge} />
      </Link>
    </li>
  );
};

/**
 * Collapsible navigation section
 */
const NavSection = ({ section, currentPath, userRole }) => {
  const [isOpen, setIsOpen] = useState(section.defaultOpen);
  const styles = SECTION_STYLES[section.type] || SECTION_STYLES[NavSectionType.CONFIG];
  const SectionIcon = section.icon;
  
  // Handle grouped sections (like Frameworks)
  const hasGroups = section.groups && section.groups.length > 0;
  const items = hasGroups ? null : section.items;
  
  // Filter items based on role
  const filterItems = (itemList) => {
    return itemList?.filter(item => {
      if (item.employeeOnly && userRole !== 'employee') return false;
      if (item.clientOnly && userRole !== 'client') return false;
      return true;
    }) || [];
  };
  
  return (
    <div className="mb-2" data-testid={`nav-section-${section.id}`}>
      {/* Section Header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          w-full flex items-center gap-3 px-4 py-2.5
          text-xs font-semibold uppercase tracking-wider
          transition-colors border-b
          ${styles.headerClass}
        `}
      >
        <SectionIcon size={14} />
        <span className="flex-1 text-left">{section.label}</span>
        <span className="text-[10px] font-normal normal-case opacity-60">
          {section.description}
        </span>
        {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>
      
      {/* Section Content */}
      {isOpen && (
        <ul className="py-1 space-y-0.5">
          {/* Overview link (if section has overviewPath) */}
          {section.overviewPath && (
            <li className="px-2">
              <Link
                to={section.overviewPath}
                data-testid={`nav-overview-${section.id}`}
                className={`
                  flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-all
                  ${isPathActive(currentPath, section.overviewPath) && currentPath === section.overviewPath
                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                    : 'text-emerald-400 hover:bg-emerald-500/10'
                  }
                `}
              >
                <LayoutDashboard size={14} className="flex-shrink-0" />
                <span className="flex-1">{section.overviewLabel || 'Overview'}</span>
              </Link>
            </li>
          )}
          
          {/* Regular items */}
          {items && filterItems(items).map(item => (
            <NavItem
              key={item.id}
              item={item}
              isActive={isPathActive(currentPath, item.path)}
              sectionType={section.type}
            />
          ))}
          
          {/* Grouped items (like Frameworks) */}
          {hasGroups && section.groups.map(group => (
            <li key={group.id} className="pt-2 first:pt-0">
              <span className="px-4 text-[10px] font-medium uppercase tracking-wider text-[#4A4A4A]">
                {group.label}
              </span>
              <ul className="mt-1 space-y-0.5">
                {filterItems(group.items).map(item => (
                  <NavItem
                    key={item.id}
                    item={item}
                    isActive={isPathActive(currentPath, item.path)}
                    sectionType={section.type}
                  />
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * Hero section for Disclosure Cycles (special treatment)
 */
const HeroSection = ({ section, currentPath }) => {
  const [isOpen, setIsOpen] = useState(true);
  const SectionIcon = section.icon;
  
  return (
    <div 
      className="mb-4 bg-gradient-to-b from-emerald-500/10 to-transparent rounded-xl border border-emerald-500/20"
      data-testid="nav-hero-section"
    >
      {/* Hero Header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-3 px-4 py-3"
      >
        <div className="p-2 rounded-lg bg-emerald-500/20">
          <SectionIcon size={18} className="text-emerald-400" />
        </div>
        <div className="flex-1 text-left">
          <h3 className="text-sm font-semibold text-white">{section.label}</h3>
          <p className="text-[10px] text-emerald-400/80">{section.description}</p>
        </div>
        {isOpen ? <ChevronDown size={14} className="text-emerald-400" /> : <ChevronRight size={14} className="text-emerald-400" />}
      </button>
      
      {/* Hero Content */}
      {isOpen && (
        <ul className="px-2 pb-3 space-y-1">
          {section.items?.map(item => {
            const Icon = item.icon;
            const isActive = isPathActive(currentPath, item.path);
            
            return (
              <li key={item.id}>
                <Link
                  to={item.path}
                  data-testid={`nav-hero-item-${item.id}`}
                  className={`
                    flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all
                    ${isActive 
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                      : 'text-[#A0A0A0] hover:bg-emerald-500/10 hover:text-emerald-400'
                    }
                  `}
                >
                  <Icon size={16} strokeWidth={1.5} />
                  <span className="flex-1 text-sm font-medium">{item.label}</span>
                  <NavBadge badge={item.badge} />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

/**
 * Main Navigation Component
 */
const Navigation = ({ userRole, setUserRole, user, onLogout, isOpen, onClose }) => {
  const location = useLocation();
  const currentPath = location.pathname + location.search;
  const [badgeData, setBadgeData] = useState({});

  // Fetch badge data on mount and periodically
  useEffect(() => {
    fetchBadgeData();
    const interval = setInterval(fetchBadgeData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  // Close the mobile drawer on route change
  useEffect(() => {
    onClose?.();
  }, [currentPath]);

  // Close the mobile drawer on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);
  
  const fetchBadgeData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const data = await apiFetch('/supplier-portal/badges');
      if (!data?._error) {
        setBadgeData(data);
      }
    } catch (error) {
      console.log('Failed to fetch badge data:', error);
    }
  };
  
  // Get dynamic badge value for a nav item
  const getDynamicBadge = (itemId) => {
    switch (itemId) {
      case 'supplier-portal':
        return badgeData.valueChain?.activeSurveys || 0;
      case 'supplier-surveys':
        return badgeData.valueChain?.pendingApprovals || 0;
      default:
        return 0;
    }
  };
  
  // Separate hero section from regular sections
  const { heroSection, regularSections, footerSection } = useMemo(() => {
    const hero = NAVIGATION_CONFIG.find(s => s.isHero);
    const footer = NAVIGATION_CONFIG.find(s => s.isFooter);
    const regular = NAVIGATION_CONFIG.filter(s => {
      if (s.isHero || s.isFooter) return false;
      if (s.adminOnly && userRole !== 'employee') return false;
      return true;
    });
    return { heroSection: hero, regularSections: regular, footerSection: footer };
  }, [userRole]);
  
  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-30 md:hidden"
          onClick={onClose}
          data-testid="nav-backdrop"
        />
      )}
      <nav
        className={`
          w-64 h-screen fixed left-0 top-0 bg-[#0A0A0A] border-r border-[#1F1F1F] z-40 flex flex-col
          transition-transform duration-300
          ${isOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0
        `}
        data-testid="main-navigation"
      >
      {/* Logo & Brand */}
      <div className="p-5 border-b border-[#1F1F1F] flex-shrink-0 flex items-center justify-between">
        <Link
          to="/dashboard"
          className="flex items-center gap-3"
          data-testid="nav-brand-logo"
        >
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 border border-emerald-500/30 flex items-center justify-center">
            <Leaf size={20} className="text-emerald-400" />
          </div>
          <div>
            <h1 className="text-base font-bold text-white tracking-tight">
              DisclosureOS
            </h1>
            <p className="text-[10px] text-emerald-400/80 uppercase tracking-widest font-medium">
              Compliance Operating System
            </p>
          </div>
        </Link>
        <button
          onClick={onClose}
          aria-label="Close navigation"
          data-testid="nav-close-btn"
          className="md:hidden p-1.5 text-[#6B6B6B] hover:text-white hover:bg-[#1F1F1F] rounded-lg transition-colors"
        >
          <X size={18} />
        </button>
      </div>

      {/* Role Selector */}
      <div className="px-4 py-3 border-b border-[#1F1F1F] flex-shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-medium uppercase tracking-wider text-[#6B6B6B]">View As</span>
          <div className="flex gap-1 flex-1">
            <button
              onClick={() => setUserRole('client')}
              data-testid="role-switch-client"
              className={`
                flex-1 px-2 py-1 rounded text-[10px] font-medium transition-colors
                ${userRole === 'client' 
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                  : 'bg-[#1F1F1F] text-[#6B6B6B] hover:text-[#A0A0A0]'
                }
              `}
            >
              Client
            </button>
            <button
              onClick={() => setUserRole('employee')}
              data-testid="role-switch-employee"
              className={`
                flex-1 px-2 py-1 rounded text-[10px] font-medium transition-colors
                ${userRole === 'employee' 
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                  : 'bg-[#1F1F1F] text-[#6B6B6B] hover:text-[#A0A0A0]'
                }
              `}
            >
              Employee
            </button>
          </div>
        </div>
      </div>

      {/* Scrollable Navigation */}
      <div className="flex-1 overflow-y-auto sidebar-scroll py-4 px-2">
        {/* Hero Section - Disclosure Cycles */}
        {heroSection && (
          <HeroSection 
            section={heroSection} 
            currentPath={currentPath} 
          />
        )}
        
        {/* Regular Sections */}
        {regularSections.map(section => (
          <NavSection
            key={section.id}
            section={section}
            currentPath={currentPath}
            userRole={userRole}
          />
        ))}
        
        {/* Footer Section - System */}
        {footerSection && (
          <div className="mt-4 pt-4 border-t border-[#1F1F1F]">
            <NavSection
              section={footerSection}
              currentPath={currentPath}
              userRole={userRole}
            />
          </div>
        )}

        {/* Help Link */}
        <div className="px-2 pt-2">
          <Link
            to="/help"
            data-testid="nav-help-link"
            className="flex items-center gap-2 px-3 py-2 text-sm text-[#6B6B6B] hover:text-white hover:bg-[#1F1F1F] rounded-lg transition-colors"
          >
            <HelpCircle size={16} strokeWidth={1.5} />
            <span>Help & Resources</span>
          </Link>
        </div>
      </div>

      {/* User Section */}
      <div className="flex-shrink-0 border-t border-[#1F1F1F] p-4 bg-[#0A0A0A]">
        {user ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 border border-emerald-500/30 flex items-center justify-center">
                <User size={16} className="text-emerald-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user.full_name}</p>
                <p className="text-[10px] text-[#6B6B6B] truncate">{user.email}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Link
                to="/profile"
                data-testid="nav-profile-btn"
                className="flex-1 px-3 py-1.5 bg-[#1F1F1F] hover:bg-[#2F2F2F] text-[#A0A0A0] text-xs font-medium rounded-md text-center transition-colors"
              >
                Profile
              </Link>
              <button
                onClick={onLogout}
                data-testid="nav-logout-btn"
                className="px-3 py-1.5 text-[#6B6B6B] hover:text-red-400 hover:bg-red-500/10 rounded-md transition-colors"
              >
                <LogOut size={14} />
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <Link
              to="/login"
              data-testid="nav-login-btn"
              className="block w-full px-3 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 text-sm font-medium rounded-md text-center transition-colors"
            >
              Sign In
            </Link>
            <Link
              to="/signup"
              data-testid="nav-signup-btn"
              className="block w-full px-3 py-2 border border-[#2F2F2F] hover:bg-[#1F1F1F] text-[#A0A0A0] text-sm font-medium rounded-md text-center transition-colors"
            >
              Create Account
            </Link>
          </div>
        )}
      </div>
      </nav>
    </>
  );
};

Navigation.propTypes = {
  userRole: PropTypes.oneOf(['client', 'employee']).isRequired,
  setUserRole: PropTypes.func.isRequired,
  user: PropTypes.shape({
    full_name: PropTypes.string,
    email: PropTypes.string,
  }),
  onLogout: PropTypes.func.isRequired,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
};

export default Navigation;
